import React, {useEffect, useState} from 'react';
import {Trans} from 'react-i18next';
import {useSelector} from 'react-redux';
import _get from 'lodash/get';
import {toast} from 'react-toastify';
import store from 'store';

import smbUtils from '../../../../utils/smbUtils';
import {getBrandId} from '../../../../utils/skincareUtils';
import {history} from '../../../../utils/redux/store';
import storeUtils from '../../../../utils/storeUtils';
import {privilegeUtils} from '../../../../utils/privilegeUtils';

import useSkincare from '../../../../components/hook/useSkincare';
import useSolutionType from '../../../../components/hook/useSolutionType';

import skincareProDao from '../../../../dao/skincareProDao';

import UpgradeSuccessModal from '../upgradeSuccessModal';
import NotifiedNote from './NotifiedNote';
import {getGroupedOptions} from './utils';
import SelectedPageHeader from './SelectedPageHeader';
import PublishBtn from './PublishBtn';
import PreviewBtns from './PreviewBtns';
import StatusNote from './StatusNote';
import _isEmpty from 'lodash/isEmpty';

export default function Header({publishStatus, dirty, type = null}) {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {isV1} = useSkincare(auth);
  const {isSkinAnalysis, isSimulation} = useSolutionType(auth);
  const {token, customerId} = smbUtils.customerInfo(auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionBtns, setActionBtns] = useState({
    preview_link: undefined,
    enable_preview: true,
    enable_generate_preview: true,
    enable_publish: true,
    last_generated: undefined,
    status: undefined,
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(
    storeUtils.getUpdatedSuccessFromVersion(customerId, 1) &&
      storeUtils.getUpdatedSuccess(customerId)
  );
  const groupedOptions = getGroupedOptions(isSkinAnalysis, isSimulation);
  const url = window.location.href
    .toString()
    .split('/')
    .pop();

  const [selectedPage, setSelectedPage] = useState(() =>
    groupedOptions
      .flatMap(g => g.options)
      .find(r => r.value.split('/').pop() === url)
  );

  const [showNotifiedNote, setShowNotifiedNote] = useState(false);
  const [latestUiVersion, setLatestUiVersion] = useState(null);

  const refreshStatus = async () => {
    const [scpUiConfigSetting] = await Promise.all([
      skincareProDao.getCustomerSetting({
        type: 'ScpUiConfigSetting',
      }),
    ]);
    const currActionBtns = _get(
      scpUiConfigSetting,
      'editing_revision.data',
      {}
    );
    const currStatus = _get(scpUiConfigSetting, 'editing_revision.status');

    if (_isEmpty(currActionBtns)) {
      toast.error(
        <Trans i18nKey="toast.error.update.version.failed">
          Failed to update, please try again.
        </Trans>
      );
      return;
    }
    if (
      _get(actionBtns, 'status') === 'Previewing' &&
      currStatus === 'Preview'
    ) {
      toast.success(
        <Trans i18nKey="p.s.uiconfiguration.preview.success">
          Your changes are ready for preview. Open "App Preview QR Code" to scan
          and check in the app.
        </Trans>
      );
    } else if (
      _get(actionBtns, 'status') === 'Publishing' &&
      currStatus === 'Publish'
    ) {
      toast.success(
        <Trans i18nKey="p.s.uiconfiguration.publish.success">
          Your changes are being processed and will take a few minutes to apply.
          Please check the app later.
        </Trans>
      );
    }
    setActionBtns(prev => ({...prev, ...currActionBtns, status: currStatus}));
    await getBrandId(token, customerId).then(res => {
      if (res && _get(res, 'brandId', '')) {
        const latestFeatureVersion = _get(res, 'latestFeatureVersion', null);
        const currentOnlineFeatureVersion = _get(
          res,
          'currentOnlineFeatureVersion',
          null
        );
        const localScpCloseAlertUiVersion = store.get(
          `scpCloseAlertUiVersion=${customerId}`
        );
        if (latestFeatureVersion && currentOnlineFeatureVersion) {
          setLatestUiVersion(String(latestFeatureVersion));
          setShowNotifiedNote(
            String(latestFeatureVersion) !==
              String(currentOnlineFeatureVersion) ||
              (localScpCloseAlertUiVersion &&
                String(latestFeatureVersion) !== localScpCloseAlertUiVersion)
          );
        }
      }
    });
  };

  const handlePublish = async () => {
    setIsSubmitting(true);
    await skincareProDao
      .publishCustomerSetting({
        type: 'ScpUiConfigSetting',
      })
      .then(res => {
        if (res && res.status && res.status === 'success') {
          setShowNotifiedNote(false);
        } else {
          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
        }
        setIsSubmitting(false);
        refreshStatus();
      })
      .catch(r => {
        toast.error(
          <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
        );
        setIsSubmitting(false);
      });
  };

  const handlePreview = async () => {
    setIsSubmitting(true);
    await skincareProDao
      .previewCustomerSetting({
        type: 'ScpUiConfigSetting',
      })
      .then(res => {
        if (res && res.status && res.status === 'success') {
          refreshStatus();
        } else {
          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
        }
        setIsSubmitting(false);
      })
      .catch(r => {
        toast.error(
          <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
        );
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (selectedPage) {
      history.push(selectedPage.value);
    }
  }, [selectedPage]);

  useEffect(() => {
    refreshStatus();
  }, [publishStatus]);

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth)
    )
  ) {
    history.push('/');
    return <div />;
  }

  return (
    <>
      {showNotifiedNote && url.includes('overview') && (
        <NotifiedNote
          handleClose={() => {
            store.set(`scpCloseAlertUiVersion=${customerId}`, latestUiVersion);
            setShowNotifiedNote(false);
          }}
        />
      )}
      {isV1 || type === 'menu' ? (
        <>
          <div className="d-flex justify-content-between mb-3">
            <h4>
              <Trans i18nKey="p.s.uiconfiguration.title">
                UI Configuration
              </Trans>
            </h4>
            <div>
              {!isV1 && (
                <PreviewBtns
                  dirty={dirty}
                  handlePreview={handlePreview}
                  actionBtns={actionBtns}
                  isSubmitting={isSubmitting}
                />
              )}
              <PublishBtn
                dirty={dirty}
                handlePublish={handlePublish}
                actionBtns={actionBtns}
                isSubmitting={isSubmitting}
              />
            </div>
          </div>
          <StatusNote
            status={_get(actionBtns, 'status')}
            handleRefresh={refreshStatus}
          />
        </>
      ) : (
        <div className="d-flex justify-content-between mb-3">
          <SelectedPageHeader
            groupedOptions={groupedOptions}
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
            dirty={dirty}
          />
        </div>
      )}
      <hr />
      <UpgradeSuccessModal
        show={showUpgradeModal}
        setShow={setShowUpgradeModal}
      />
    </>
  );
}
